import { labelDovolenaRequestType } from './dovolenaRequestType'
import type { CalendarEvent } from './icsCalendar'
import { loadICSCalendar } from './icsCalendar'
import type { User } from '../types'
import { hasFinancistAccess } from './roleFinancialAccess'
import type { Ticket, PurchaseRequest, FkspRequest, DovolenaRequest } from '../types'
import type { TravelOrder } from './apiPhpAdapter'
import { getPurchaseStatusLabel } from './purchaseRequestStatus'
import {
  getSetting,
  loadTicketsForCabinet,
  loadPropertyMoveTicketsForCabinet,
  loadPurchaseRequestsForCabinet,
  loadTravelOrders,
  loadDovolenaRequests,
  loadFkspRequests,
} from './apiPhpAdapter'

/** Kalendář akcí školy (stejné jako CalendarView). */
export const DEFAULT_SCHOOL_EVENTS_ICS =
  'https://outlook.office365.com/owa/calendar/b86242d5bf2d4496a89c04f9eaf4cc69@iss-cheb.cz/6d0809beabcb451898dddfc17196b3433773793055790110233/calendar.ics'

/** Kalendář využití školních prostor (stejné jako PublicEventsCalendar). */
export const DEFAULT_SCHOOL_PREMISES_ICS =
  'https://outlook.office365.com/owa/calendar/59f1ce71c3a740f48fd594d1080f24d2@iss-cheb.cz/781ecaad84f840a0afa276b8fcf1729a16416940637749074778/calendar.ics'

export type ActivityKind =
  | 'ticket'
  | 'property_move'
  | 'purchase'
  | 'travel'
  | 'dovolena'
  | 'fksp'

export type CabinetActivityRow = {
  id: string
  kind: ActivityKind
  title: string
  subtitle?: string
  /** Stav pro sloupec tabulky (ne parsovat z title). */
  status: string
  path: string
  /** YYYY-MM-DD */
  rangeStart: string
  rangeEnd: string
  /** Žádost jsem založil (žadatel / vlastní CP / …). */
  cabinetIsAuthor?: boolean
  /**
   * Přehled kabinetu: od mě se teď vyžaduje akce (schválení / zpracování),
   * kterou jsem ještě neudělal. Ne historická účast.
   */
  cabinetIsProcessRole?: boolean
  /** Krátký popis účasti pro filtry a vyhledávání. */
  cabinetInvolvementHint?: string
}

export type MonthBounds = { year: number; month: number }

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

export function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function activityRowOnDay(
  row: CabinetActivityRow,
  year: number,
  month: number,
  day: number
): boolean {
  const d = `${year}-${pad2(month + 1)}-${pad2(day)}`
  return row.rangeStart <= d && row.rangeEnd >= d
}

function ymdFromIso(iso: string | undefined | null): string | null {
  if (!iso) return null
  const s = String(iso).trim()
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : null
}

function rangeInclusive(startYmd: string, endYmd: string): { start: string; end: string } {
  if (startYmd <= endYmd) return { start: startYmd, end: endYmd }
  return { start: endYmd, end: startYmd }
}

function parseYmdParts(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number)
  return { y, m0: m - 1, d }
}

export function toCalendarEvent(
  id: string,
  subject: string,
  startYmd: string,
  endYmdInclusive: string
): CalendarEvent {
  const { start, end } = rangeInclusive(startYmd, endYmdInclusive)
  const a = parseYmdParts(start)
  const b = parseYmdParts(end)
  const startDt = new Date(Date.UTC(a.y, a.m0, a.d, 12, 0, 0, 0))
  const endExclusive = new Date(Date.UTC(b.y, b.m0, b.d + 1, 12, 0, 0, 0))
  return {
    id,
    subject,
    start: { dateTime: startDt.toISOString(), timeZone: 'UTC' },
    end: { dateTime: endExclusive.toISOString(), timeZone: 'UTC' },
  }
}

export function monthOverlapsRange(
  year: number,
  month: number,
  startYmd: string,
  endYmd: string
): boolean {
  const m0 = new Date(year, month, 1)
  const m1 = new Date(year, month + 1, 0)
  const a = toYmd(m0)
  const b = toYmd(m1)
  const { start, end } = rangeInclusive(startYmd, endYmd)
  return start <= b && end >= a
}

/** Český název modulu pro tabulku / filtry. */
export function activityModuleLabel(kind: ActivityKind): string {
  const m: Record<ActivityKind, string> = {
    ticket: 'Závady',
    property_move: 'Majetek',
    purchase: 'Nákupy',
    travel: 'Cestovní příkaz',
    dovolena: 'Žádosti o nepřítomnost',
    fksp: 'FKSP',
  }
  return m[kind] || kind
}

export function activityStatusFromRow(row: CabinetActivityRow): string {
  return (row.status && row.status.trim()) || '—'
}

function czInvolvementHint(parts: string[]): string | undefined {
  const u = [...new Set(parts.filter(Boolean))]
  if (u.length === 0) return undefined
  return u.join(', ')
}

function isRealFinancist(user: User): boolean {
  return user.role === 'financist' || user.role === 'referent_eko'
}

function isItAdmin(user: User): boolean {
  return user.role === 'admin' && user.admin_type === 'spravce_it'
}

/** Hotové / uzavřené stavy — v kabinetu neukazovat (jsou v modulech). */
const TICKET_DONE = new Set(['completed', 'rejected'])
const PURCHASE_DONE = new Set([
  'completed',
  'rejected',
  'rejected_by_economist',
  'rejected_by_director',
  'rejected_by_vedouci',
  'rejected_by_leader',
  'rejected_by_referent',
  'cancelled_by_requester',
  'cancelled_by_leader',
])
const TRAVEL_DONE = new Set(['rejected', 'completed', 'closed'])
const FKSP_DONE = new Set(['personalista_rejected', 'director_rejected', 'director_approved'])

function isTicketStillOpen(status: string): boolean {
  return !TICKET_DONE.has(status)
}

function isPurchaseStillOpen(status: string): boolean {
  return !PURCHASE_DONE.has(status)
}

function isTravelStillOpen(status: string): boolean {
  return !TRAVEL_DONE.has(status)
}

/** FKSP proces ještě běží (včetně čekání na ekonoma po schválení ředitelem). */
function isFkspProcessOpen(f: FkspRequest): boolean {
  const st = String(f.status || '')
  if (st === 'personalista_rejected' || st === 'director_rejected') return false
  if (st === 'director_approved') return !f.financist_processed_at
  return !FKSP_DONE.has(st)
}

/** Žádost o nepřítomnost ještě relevantní pro Přehled (ne zamítnuté). */
function isDovolenaNotRejected(d: DovolenaRequest): boolean {
  return d.status !== 'rejected'
}

function ticketStatusCz(s: string): string {
  const map: Record<string, string> = {
    new: 'Nový',
    in_progress: 'V řešení',
    completed: 'Hotovo',
    rejected: 'Zamítnuto',
    awaiting_recipient_response: 'Čeká na příjemce',
    recipient_accepted_self_transport: 'Přijato',
    recipient_needs_transport: 'Doprava',
    awaiting_transport: 'Doprava',
  }
  return map[s] || s
}

function travelStatusCz(s: string): string {
  const map: Record<string, string> = {
    draft: 'Koncept',
    pending_approval: 'Ke schválení',
    approved: 'Schváleno',
    rejected: 'Zamítnuto',
    in_progress: 'Probíhá',
    pending_return_confirmation: 'Návrat',
    pending_completion: 'K uzavření',
    pending_settlement_supervisor: 'Vyúčtování — vedoucí',
    completed: 'Dokončeno',
    closed: 'Uzavřeno',
  }
  return map[s] || s
}

function fkspStatusCz(s: string): string {
  const map: Record<string, string> = {
    new: 'Nová',
    personalista_pending: 'Personalista',
    personalista_approved: 'Personalista OK',
    personalista_rejected: 'Personalista zamítl',
    zocmos_pending: 'ZO ČMOS',
    zocmos_approved: 'ZO ČMOS OK',
    zocmos_rejected: 'ZO ČMOS zamítl',
    director_pending: 'Ředitel',
    director_approved: 'Schváleno ředitelem',
    director_rejected: 'Zamítnuto ředitelem',
  }
  return map[s] || String(s || '').replace(/_/g, ' ') || '—'
}

function ticketCabinetFlags(
  t: Ticket,
  uid: string
): Pick<CabinetActivityRow, 'cabinetIsAuthor' | 'cabinetIsProcessRole' | 'cabinetInvolvementHint'> {
  const author = t.requester_id === uid
  const roles: string[] = []
  // Řešitel — dokud závada není hotová
  if (t.executor_id === uid && isTicketStillOpen(t.status)) {
    roles.push('řešitel — ke zpracování')
  }
  // Správce — přiřadit řešitele u nové závady
  if (t.admin_id === uid && t.status === 'new') {
    roles.push('správce — přiřadit')
  }
  return {
    cabinetIsAuthor: author,
    cabinetIsProcessRole: roles.length > 0,
    cabinetInvolvementHint: czInvolvementHint([...(author ? ['žadatel'] : []), ...roles]),
  }
}

function ticketDeepPath(t: Ticket, user: User): string {
  const uid = user.id
  const idQ = `id=${encodeURIComponent(t.id)}`
  if (t.executor_id === uid) return `/app?role=executor&${idQ}`
  if (t.admin_id === uid) {
    const at = user.admin_type ? `&adminType=${encodeURIComponent(user.admin_type)}` : ''
    return `/app?role=admin&${idQ}${at}`
  }
  if (t.requester_id === uid) return `/app?role=requester&${idQ}`
  if (t.assigned_to_id === uid) return `/app?role=requester&${idQ}`
  return `/app/select-role?${idQ}`
}

function propertyMoveCabinetFlags(
  t: Ticket,
  uid: string
): Pick<CabinetActivityRow, 'cabinetIsAuthor' | 'cabinetIsProcessRole' | 'cabinetInvolvementHint'> {
  const author = t.requester_id === uid
  const roles: string[] = []
  const st = t.status
  // Příjemce — jen dokud má odpovědět
  if (
    t.assigned_to_id === uid &&
    (st === 'awaiting_recipient_response' || st === 'new')
  ) {
    roles.push('příjemce — ke odpovědi')
  }
  // Správce / doprava — jen stavy, kde se od něj čeká akce
  const adminId = (t as { admin_id?: string }).admin_id
  const needsAdminAction =
    st === 'recipient_needs_transport' ||
    st === 'awaiting_transport' ||
    st === 'recipient_accepted_self_transport' ||
    st === 'in_progress'
  if (needsAdminAction && (adminId === uid || (!author && t.assigned_to_id !== uid))) {
    roles.push('správce — ke zpracování')
  }
  return {
    cabinetIsAuthor: author,
    cabinetIsProcessRole: roles.length > 0,
    cabinetInvolvementHint: czInvolvementHint([...(author ? ['žadatel'] : []), ...roles]),
  }
}

function propertyDeepPath(t: Ticket, uid: string): string {
  const idQ = `id=${encodeURIComponent(t.id)}`
  if (t.requester_id === uid || t.assigned_to_id === uid) {
    return `/property?role=requester&${idQ}`
  }
  return `/property?role=property_referent&${idQ}`
}

function purchaseCabinetFlags(
  pr: PurchaseRequest,
  user: User
): Pick<CabinetActivityRow, 'cabinetIsAuthor' | 'cabinetIsProcessRole' | 'cabinetInvolvementHint'> {
  const uid = user.id
  const author = pr.created_by_id === uid
  const roles: string[] = []
  const st = pr.status

  // Jen aktuálně vyžadovaná akce — ne historické schválení
  if (pr.supervisor_id === uid && st === 'pending_leader') {
    roles.push('vedoucí — ke schválení')
  }
  if (isRealFinancist(user) && (st === 'new' || st === 'deferred')) {
    roles.push('ekonom — ke zpracování')
  }
  if (user.role === 'director' && st === 'approved_by_economist') {
    roles.push('ředitel — ke schválení')
  }
  if (
    (user.role === 'referent' || isRealFinancist(user)) &&
    (st === 'approved_by_director' ||
      st === 'accepted_by_referent' ||
      st === 'ordered_by_referent' ||
      st === 'ready_by_referent')
  ) {
    roles.push('referent — ke zpracování')
  }

  return {
    cabinetIsAuthor: author,
    cabinetIsProcessRole: roles.length > 0,
    cabinetInvolvementHint: czInvolvementHint([...(author ? ['žadatel'] : []), ...roles]),
  }
}

function purchaseDeepPath(pr: PurchaseRequest, user: User): string {
  const uid = user.id
  const idQ = `id=${encodeURIComponent(pr.id)}`
  let role = 'zadatel'
  if (
    pr.supervisor_id === uid ||
    pr.approved_by_leader_id === uid ||
    pr.rejected_by_leader_id === uid ||
    (pr.status === 'pending_leader' && pr.supervisor_id === uid)
  ) {
    role = 'vedouci'
  } else if (
    pr.approved_by_economist_id === uid ||
    pr.rejected_by_economist_id === uid ||
    pr.deferred_by_id === uid ||
    (isRealFinancist(user) && (pr.status === 'new' || pr.status === 'deferred'))
  ) {
    role = 'financist'
  } else if (
    pr.approved_by_director_id === uid ||
    pr.rejected_by_director_id === uid ||
    (user.role === 'director' && pr.status === 'approved_by_economist')
  ) {
    role = 'director'
  } else if (
    pr.accepted_by_referent_id === uid ||
    pr.ordered_by_referent_id === uid ||
    pr.ready_by_referent_id === uid ||
    pr.rejected_by_referent_id === uid ||
    pr.status === 'approved_by_director'
  ) {
    role = 'referent'
  } else if (pr.created_by_id === uid) {
    role = 'zadatel'
  }
  return `/purchase-requests?role=${role}&${idQ}`
}

function fkspCabinetFlags(
  f: FkspRequest,
  user: User
): Pick<CabinetActivityRow, 'cabinetIsAuthor' | 'cabinetIsProcessRole' | 'cabinetInvolvementHint'> {
  const author = f.requester_id === user.id
  const roles: string[] = []

  if (user.role === 'personalista' && f.status === 'personalista_pending') {
    roles.push('personalista — ke kontrole')
  }
  if (user.role === 'zocmos' && f.status === 'zocmos_pending') {
    roles.push('ZO ČMOS — ke kontrole')
  }
  if (
    user.role === 'director' &&
    (f.status === 'director_pending' || f.status === 'zocmos_approved' || f.status === 'zocmos_rejected')
  ) {
    roles.push('ředitel — ke schválení')
  }
  if (isRealFinancist(user) && f.status === 'director_approved' && !f.financist_processed_at) {
    roles.push('ekonom — ke zpracování')
  }

  return {
    cabinetIsAuthor: author,
    cabinetIsProcessRole: roles.length > 0,
    cabinetInvolvementHint: czInvolvementHint([...(author ? ['žadatel'] : []), ...roles]),
  }
}

function fkspDeepPath(f: FkspRequest, user: User): string {
  const idQ = `id=${encodeURIComponent(f.id)}`
  if (f.requester_id === user.id && !fkspCabinetFlags(f, user).cabinetIsProcessRole) {
    return `/fksp?${idQ}`
  }
  if (
    user.role === 'personalista' ||
    f.personalista_id === user.id ||
    f.status === 'personalista_pending'
  ) {
    if (user.role === 'personalista' || isItAdmin(user)) return `/fksp/personalista?${idQ}`
  }
  if (user.role === 'zocmos' || f.zocmos_id === user.id || f.status === 'zocmos_pending') {
    if (user.role === 'zocmos' || isItAdmin(user)) return `/fksp/zocmos?${idQ}`
  }
  if (
    user.role === 'director' ||
    f.director_id === user.id ||
    f.status === 'director_pending' ||
    f.status === 'zocmos_approved' ||
    f.status === 'zocmos_rejected'
  ) {
    if (user.role === 'director' || isItAdmin(user)) return `/fksp/director?${idQ}`
  }
  if (isRealFinancist(user) || f.financist_processed_by === user.id || hasFinancistAccess(user)) {
    if (isRealFinancist(user) || isItAdmin(user)) return `/fksp/ekonom?${idQ}`
  }
  if (f.requester_id === user.id) return `/fksp?${idQ}`
  return `/fksp/select-role?${idQ}`
}

function travelSupervisorActionPending(o: TravelOrder, uid: string): boolean {
  if (o.supervisor_id !== uid) return false
  return (
    o.status === 'pending_approval' ||
    o.status === 'pending_settlement_supervisor' ||
    o.status === 'pending_return_confirmation'
  )
}

function travelFinancistActionPending(o: TravelOrder, user: User): boolean {
  return (
    isRealFinancist(user) &&
    !o.financist_processed_at &&
    o.status === 'pending_completion'
  )
}

function travelDeepPath(o: TravelOrder, user: User): string {
  const idQ = `id=${encodeURIComponent(o.id)}`
  if (o.employee_id === user.id) return `/travel-orders?mode=employee&${idQ}`
  if (o.supervisor_id === user.id) return `/travel-orders?mode=supervisor&${idQ}`
  if (isRealFinancist(user) || hasFinancistAccess(user)) {
    return `/travel-orders?mode=financist&${idQ}`
  }
  return `/travel-orders?${idQ}`
}

function dovolenaDeepPath(d: DovolenaRequest, user: User): string {
  const idQ = `id=${encodeURIComponent(d.id)}`
  if (d.requester_id === user.id) return `/dovolena?role=zadatel&${idQ}`
  if (d.supervisor_id === user.id) return `/dovolena?role=vedouci&${idQ}`
  if (user.role === 'personalista' || d.personalista_processed_by === user.id) {
    return `/dovolena?role=personalista&${idQ}`
  }
  if (user.role === 'spravce_rozvrhu') return `/dovolena?role=spravce_rozvrhu&${idQ}`
  return `/dovolena?role=zadatel&${idQ}`
}

export async function loadSchoolEventsForMonth(
  year: number,
  month: number,
  icsUrlOverride: string | null
): Promise<{ events: CalendarEvent[]; error: string | null }> {
  const url = (icsUrlOverride && icsUrlOverride.trim()) || DEFAULT_SCHOOL_EVENTS_ICS
  const startDate = new Date(year, month, 1, 0, 0, 0, 0)
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999)
  try {
    const events = await loadICSCalendar(url, startDate, endDate)
    return { events, error: null }
  } catch (e: any) {
    return { events: [], error: e?.message || 'Kalendář se nepodařilo načíst' }
  }
}

export async function loadPremisesEventsForMonth(
  year: number,
  month: number,
  icsUrlOverride?: string | null
): Promise<{ events: CalendarEvent[]; error: string | null }> {
  const url = (icsUrlOverride && icsUrlOverride.trim()) || DEFAULT_SCHOOL_PREMISES_ICS
  const startDate = new Date(year, month, 1, 0, 0, 0, 0)
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999)
  try {
    const events = await loadICSCalendar(url, startDate, endDate)
    return { events, error: null }
  } catch (e: any) {
    return { events: [], error: e?.message || 'Kalendář prostor se nepodařilo načíst' }
  }
}

/** Přehled: jen moje otevřené žádosti, nebo fronta čekající na mou akci. */
function isPersonallyRelevant(row: CabinetActivityRow): boolean {
  return row.cabinetIsAuthor === true || row.cabinetIsProcessRole === true
}

async function collectCabinetActivityRows(
  user: User,
  monthBounds: MonthBounds | null
): Promise<CabinetActivityRow[]> {
  const rows: CabinetActivityRow[] = []
  const seenIds = new Set<string>()

  const add = (r: CabinetActivityRow) => {
    if (!isPersonallyRelevant(r)) return
    if (seenIds.has(r.id)) return
    if (
      monthBounds &&
      !monthOverlapsRange(monthBounds.year, monthBounds.month, r.rangeStart, r.rangeEnd)
    ) {
      return
    }
    seenIds.add(r.id)
    rows.push(r)
  }

  type LoadResult<T> = { ok: true; value: T } | { ok: false }

  const settle = async <T>(p: Promise<T>): Promise<LoadResult<T>> => {
    try {
      return { ok: true, value: await p }
    } catch {
      return { ok: false }
    }
  }

  const canTravelFinancist = isRealFinancist(user) || user.role === 'director' || isItAdmin(user)

  const [
    ticketsRes,
    propertyRes,
    purchaseRes,
    travelEmpRes,
    travelSupRes,
    travelFinRes,
    dovolenaRes,
    fkspRes,
  ] = await Promise.all([
    settle(loadTicketsForCabinet(500)),
    settle(loadPropertyMoveTicketsForCabinet()),
    settle(loadPurchaseRequestsForCabinet()),
    settle(loadTravelOrders('employee')),
    settle(loadTravelOrders('supervisor')),
    canTravelFinancist ? settle(loadTravelOrders('financist')) : Promise.resolve({ ok: false as const }),
    settle(loadDovolenaRequests()),
    settle(loadFkspRequests(undefined, { limit: 500 })),
  ])

  if (ticketsRes.ok) {
    for (const t of ticketsRes.value) {
      if (!isTicketStillOpen(t.status)) continue
      const day = ymdFromIso(t.created_at)
      if (!day) continue
      const cab = ticketCabinetFlags(t, user.id)
      const hint = cab.cabinetInvolvementHint
      const st = ticketStatusCz(t.status)
      add({
        id: `ticket:${t.id}`,
        kind: 'ticket',
        title: `Závada · ${(t.description || '').replace(/\s+/g, ' ').trim().slice(0, 72) || t.building_name || t.id}`,
        subtitle: hint ? `Účast: ${hint}` : undefined,
        status: st,
        path: ticketDeepPath(t, user),
        rangeStart: day,
        rangeEnd: day,
        ...cab,
      })
    }
  }

  if (propertyRes.ok) {
    for (const t of propertyRes.value) {
      if (!isTicketStillOpen(t.status)) continue
      const day = ymdFromIso(t.created_at)
      if (!day) continue
      const cab = propertyMoveCabinetFlags(t, user.id)
      const hint = cab.cabinetInvolvementHint
      const st = ticketStatusCz(t.status)
      add({
        id: `prop:${t.id}`,
        kind: 'property_move',
        title: `Přesun majetku · ${(t as { item_name?: string }).item_name || (t as { inventory_number?: string }).inventory_number || t.id}`,
        subtitle: hint ? `Účast: ${hint}` : undefined,
        status: st,
        path: propertyDeepPath(t, user.id),
        rangeStart: day,
        rangeEnd: day,
        ...cab,
      })
    }
  }

  if (purchaseRes.ok) {
    for (const pr of purchaseRes.value) {
      if (!isPurchaseStillOpen(pr.status)) continue
      const day = ymdFromIso(pr.created_at)
      if (!day) continue
      const cab = purchaseCabinetFlags(pr, user)
      const hint = cab.cabinetInvolvementHint
      add({
        id: `purchase:${pr.id}`,
        kind: 'purchase',
        title: `Nákup · ${pr.subject?.slice(0, 60) || pr.id}`,
        subtitle: hint ? `Účast: ${hint}` : undefined,
        status: getPurchaseStatusLabel(pr.status),
        path: purchaseDeepPath(pr, user),
        rangeStart: day,
        rangeEnd: day,
        ...cab,
      })
    }
  }

  if (travelEmpRes.ok) {
    for (const o of travelEmpRes.value) {
      if (!isTravelStillOpen(o.status)) continue
      const dep = ymdFromIso(o.departure_date) || ymdFromIso(o.created_at)
      if (!dep) continue
      const ret = ymdFromIso(o.expected_return_date || o.actual_return_date) || dep
      const author = o.employee_id === user.id
      if (!author) continue
      add({
        id: `travel:${o.id}`,
        kind: 'travel',
        title: `Cestovní příkaz · ${o.destination_place || o.purpose?.slice(0, 48) || o.order_number}`,
        subtitle: undefined,
        status: travelStatusCz(o.status),
        path: travelDeepPath(o, user),
        rangeStart: dep,
        rangeEnd: ret,
        cabinetIsAuthor: true,
        cabinetIsProcessRole: false,
        cabinetInvolvementHint: 'žadatel (CP)',
      })
    }
  }

  if (travelSupRes.ok) {
    for (const o of travelSupRes.value) {
      if (o.employee_id === user.id) continue
      if (!travelSupervisorActionPending(o, user.id)) continue
      const dep = ymdFromIso(o.departure_date) || ymdFromIso(o.created_at)
      if (!dep) continue
      const ret = ymdFromIso(o.expected_return_date || o.actual_return_date) || dep
      add({
        id: `travel:${o.id}`,
        kind: 'travel',
        title: `CP — schvalování · ${(o.employee_name || '').trim()}`.trim(),
        subtitle: o.destination_place || undefined,
        status: travelStatusCz(o.status),
        path: travelDeepPath(o, user),
        rangeStart: dep,
        rangeEnd: ret,
        cabinetIsAuthor: false,
        cabinetIsProcessRole: true,
        cabinetInvolvementHint: 'schvalovatel — ke zpracování',
      })
    }
  }

  if (travelFinRes.ok) {
    for (const o of travelFinRes.value) {
      if (o.employee_id === user.id) continue
      if (!travelFinancistActionPending(o, user)) continue
      const dep = ymdFromIso(o.departure_date) || ymdFromIso(o.created_at)
      if (!dep) continue
      const ret = ymdFromIso(o.expected_return_date || o.actual_return_date) || dep
      add({
        id: `travel:${o.id}`,
        kind: 'travel',
        title: `CP — ekonom · ${(o.employee_name || '').trim()}`.trim(),
        subtitle: o.destination_place || undefined,
        status: travelStatusCz(o.status),
        path: travelDeepPath(o, user),
        rangeStart: dep,
        rangeEnd: ret,
        cabinetIsAuthor: false,
        cabinetIsProcessRole: true,
        cabinetInvolvementHint: 'ekonom — ke zpracování',
      })
    }
  }

  if (dovolenaRes.ok) {
    const dash = dovolenaRes.value
    const pooled: DovolenaRequest[] = [
      ...(dash.mine || []),
      ...(dash.pendingApproval || []),
    ]
    // Personalista: jen fronta ke zpracování (ne už zpracované)
    if (user.role === 'personalista') {
      for (const d of dash.allRequests || []) {
        if (d.status === 'approved' && !d.personalista_processed_at) {
          pooled.push(d)
        }
      }
    } else if (dash.allRequests) {
      for (const d of dash.allRequests) {
        if (d.requester_id === user.id || d.created_by_id === user.id) {
          pooled.push(d)
        } else if (d.supervisor_id === user.id && d.status === 'new') {
          pooled.push(d)
        }
      }
    }

    const seen = new Set<string>()
    for (const d of pooled) {
      if (seen.has(d.id)) continue
      seen.add(d.id)
      if (!isDovolenaNotRejected(d)) continue
      const a = ymdFromIso(d.date_from)
      const b = ymdFromIso(d.date_to) || a
      if (!a) continue
      const st =
        d.status === 'approved' ? 'Schváleno' : d.status === 'rejected' ? 'Zamítnuto' : 'Nové'
      const typShort = labelDovolenaRequestType(String(d.request_type || 'dovolena'))
      const author = d.requester_id === user.id || d.created_by_id === user.id
      // Žadatel: jen nové (ne schválené / zamítnuté) — schválené v Přehledu nepatří
      const showAsAuthor = author && d.status === 'new'
      const roles: string[] = []
      if (showAsAuthor) roles.push('žadatel')
      const pendingSupervisor = d.supervisor_id === user.id && d.status === 'new'
      if (pendingSupervisor) roles.push('schvalovatel — ke schválení')
      const pendingPersonalista =
        user.role === 'personalista' && d.status === 'approved' && !d.personalista_processed_at
      if (pendingPersonalista) roles.push('personalista — ke zpracování')
      const process = pendingSupervisor || pendingPersonalista
      if (!showAsAuthor && !process) continue
      add({
        id: `dov:${d.id}`,
        kind: 'dovolena',
        title: `${typShort} · ${d.requester_name || ''}`.trim(),
        subtitle: `${d.date_from?.slice(0, 10) || ''} – ${d.date_to?.slice(0, 10) || ''}`,
        status: st,
        path: dovolenaDeepPath(d, user),
        rangeStart: a,
        rangeEnd: b || a,
        cabinetIsAuthor: showAsAuthor,
        cabinetIsProcessRole: process,
        cabinetInvolvementHint: czInvolvementHint(roles),
      })
    }
  }

  if (fkspRes.ok) {
    for (const f of fkspRes.value) {
      if (!isFkspProcessOpen(f)) continue
      const day = ymdFromIso(f.created_at)
      if (!day) continue
      const typ = f.type === 'benefity' ? 'Benefity' : 'Připojištění'
      const cab = fkspCabinetFlags(f, user)
      if (!cab.cabinetIsAuthor && !cab.cabinetIsProcessRole) continue
      const hint = cab.cabinetInvolvementHint
      add({
        id: `fksp:${f.id}`,
        kind: 'fksp',
        title: `FKSP · ${typ}`,
        subtitle: hint ? `Účast: ${hint}` : undefined,
        status: fkspStatusCz(String(f.status || '')),
        path: fkspDeepPath(f, user),
        rangeStart: day,
        rangeEnd: day,
        ...cab,
      })
    }
  }

  rows.sort((a, b) => b.rangeStart.localeCompare(a.rangeStart))
  return rows
}

/** Osobní žádosti / záznamy pro Můj kabinet (bez admin „vidím vše“). */
export async function loadCabinetActivityRowsForUser(user: User): Promise<CabinetActivityRow[]> {
  return collectCabinetActivityRows(user, null)
}

export function buildMonthStats(
  rows: CabinetActivityRow[],
  year: number,
  month: number
): { key: string; label: string; count: number }[] {
  const inMonth = (r: CabinetActivityRow) =>
    monthOverlapsRange(year, month, r.rangeStart, r.rangeEnd)
  const byKind = (k: ActivityKind) => rows.filter((x) => x.kind === k).filter(inMonth).length
  return [
    { key: 'ticket', label: 'Závady', count: byKind('ticket') },
    { key: 'property_move', label: 'Majetek', count: byKind('property_move') },
    { key: 'purchase', label: 'Nákupy', count: byKind('purchase') },
    { key: 'travel', label: 'Cestovní', count: byKind('travel') },
    { key: 'dovolena', label: 'Žádosti o nepřítomnost', count: byKind('dovolena') },
    { key: 'fksp', label: 'FKSP', count: byKind('fksp') },
  ]
}

/** @deprecated Použijte loadCabinetActivityRowsForUser + buildMonthStats; ponecháno pro kompatibilitu. */
export async function loadMyCabinetActivity(
  user: User,
  year: number,
  month: number
): Promise<{
  rows: CabinetActivityRow[]
  events: CalendarEvent[]
  stats: { key: string; label: string; count: number }[]
}> {
  const rows = await collectCabinetActivityRows(user, { year, month })
  const events = rows.map((r) => toCalendarEvent(r.id, r.title, r.rangeStart, r.rangeEnd))
  const stats = buildMonthStats(rows, year, month)
  return { rows, events, stats }
}

export async function resolveSchoolIcsUrl(): Promise<string> {
  try {
    const u = await getSetting('calendar_url')
    if (u && u.trim()) return u.trim()
  } catch {
    /* */
  }
  return DEFAULT_SCHOOL_EVENTS_ICS
}
